
// shared client-side helpers for the FinanceStrip block
//	dev builds talk to a locally running widgets dev server (pnpm dev in
//	sites/ADH/widgets); production builds (www + islands) use the live service
export const FINANCE_API_URL = import.meta.env?.DEV
	? "http://localhost:5173/finance/api"
	: "https://widgets.adh.reperio.news/finance/api";

export const DEFAULT_CURRENCIES = ["EUR", "USD", "GBP", "CHF", "XAU"];

// BNR reference-rate codes -> Romanian display names (ordered as BNR lists them)
export const CURRENCY_NAMES = {
	AED: "Dirham EAU", AUD: "Dolar australian", BRL: "Real brazilian", CAD: "Dolar canadian",
	CHF: "Franc elvețian", CNY: "Yuan chinezesc", CZK: "Coroană cehă", DKK: "Coroană daneză",
	EGP: "Liră egipteană", EUR: "Euro", GBP: "Liră sterlină", HKD: "Dolar Hong Kong",
	HUF: "Forint maghiar", IDR: "Rupie indoneziană", ILS: "Shekel israelian", INR: "Rupie indiană",
	ISK: "Coroană islandeză", JPY: "Yen japonez", KRW: "Won sud-coreean", MDL: "Leu moldovenesc",
	MXN: "Peso mexican", MYR: "Ringgit malaezian", NOK: "Coroană norvegiană", NZD: "Dolar neozeelandez",
	PHP: "Peso filipinez", PLN: "Zlot polonez", RSD: "Dinar sârbesc", RUB: "Rublă rusească",
	SEK: "Coroană suedeză", SGD: "Dolar Singapore", THB: "Baht thailandez", TRY: "Liră turcească",
	UAH: "Grivnă ucraineană", USD: "Dolar american", XAU: "Aur (1 gram)", XDR: "DST (FMI)",
	ZAR: "Rand sud-african",
};

export const FUEL_LABELS = {
	benzina_standard: "Benzină standard",
	benzina_premium: "Benzină premium",
	motorina_standard: "Motorină standard",
	motorina_premium: "Motorină premium",
	gpl: "GPL",
};

// the two grades pinned on the strip; everything else lives in the detail view
export const STRIP_FUELS = [
	{ key: "benzina_standard", label: "Benzină" },
	{ key: "motorina_standard", label: "Motorină" },
];

export const INDEX_LABELS = {
	robor3m: "ROBOR 3M",
	robor6m: "ROBOR 6M",
	ircc: "IRCC",
};

export const INDEX_DESCRIPTIONS = {
	robor3m: "Rata dobânzii interbancare la 3 luni — referință pentru creditele vechi în lei",
	robor6m: "Rata dobânzii interbancare la 6 luni",
	ircc: "Indicele de referință pentru creditele consumatorilor — referință pentru creditele noi în lei",
};

// ro-RO formatting: decimal comma, non-breaking thin separators
const formatters = {};
export function formatNumber(value, decimals) {
	if (typeof value !== "number" || !isFinite(value)) {
		return "—";
	}
	const key = decimals;
	if (!formatters[key]) {
		formatters[key] = new Intl.NumberFormat("ro-RO", {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		});
	}
	return formatters[key].format(value);
}

// gold is quoted per gram in the hundreds; four decimals would read as noise
export function currencyDecimals(code) {
	return code === "XAU" ? 2 : 4;
}

// "20 aug." style short date from an ISO yyyy-mm-dd string; returns "" when unparsable
export function formatShortDate(iso) {
	if (!iso) {
		return "";
	}
	const date = new Date(`${iso}T12:00:00`);
	if (isNaN(date.getTime())) {
		return "";
	}
	return new Intl.DateTimeFormat("ro-RO", { day: "numeric", month: "short" }).format(date);
}

// null means "no usable data" — callers must hide themselves, never render an
// error. Either section (bnr/fuel) may be null on its own.
export async function fetchFinance() {
	try {
		const signal =
			typeof AbortSignal !== "undefined" && AbortSignal.timeout
				? AbortSignal.timeout(10000)
				: undefined;
		const res = await fetch(FINANCE_API_URL, { signal });
		if (!res.ok) {
			return null;
		}
		const json = await res.json();
		if (!json || json.ok !== true) {
			return null;
		}
		const bnr =
			json.bnr && Array.isArray(json.bnr.rates) && json.bnr.rates.length
				? json.bnr
				: null;
		const fuel =
			json.fuel && json.fuel.prices && Object.keys(json.fuel.prices).length
				? json.fuel
				: null;
		if (!bnr && !fuel) {
			return null;
		}
		return { bnr, fuel };
	} catch (e) {
		return null;
	}
}

// "Știri recente" in the detail view: default tags per section (editor-
// overridable in the block config). Keys match the strip group keys. Matched as ANY-OF against the current site’s
// content tags; accents/case are normalised server-side.
export const NEWS_LIMIT = 4;
export const NEWS_SECTIONS = {
	currency: { label: "Curs BNR", tags: ["curs valutar", "bnr", "curs bnr", "euro", "dolar", "leu"] },
	fuel: { label: "Carburanți", tags: ["motorina", "benzina", "carburanti", "pret carburanti", "combustibil"] },
	index: { label: "Indici", tags: ["robor", "ircc", "bursa", "bvb", "dobanzi", "credite"] },
};

// missing config -> defaults; an explicit (possibly empty) array wins
export function resolveNewsTags(news) {
	const out = {};
	for (const key of Object.keys(NEWS_SECTIONS)) {
		const cfg = news && news[key];
		out[key] = Array.isArray(cfg?.tags) ? cfg.tags : NEWS_SECTIONS[key].tags;
	}
	return out;
}

// per-page memo: reopening the popup / switching tabs never refetches a
// successful result; failures are NOT memoized so a later open can retry
const newsCache = new Map();

// recent published items for a tag set, via the same-origin www listing
// endpoint. Never throws: [] on any failure — the list simply stays hidden.
export async function fetchSectionNews(tags, limit = NEWS_LIMIT, domain) {
	//	www CSV-joins array params, so a comma inside a tag would split it
	const clean = (Array.isArray(tags) ? tags : [])
		.map((t) => (typeof t === "string" ? t.replace(/,/g, " ").trim() : ""))
		.filter(Boolean);
	if (!clean.length || !domain) {
		return [];
	}
	const query = {
		tags: clean,
		domain: [domain],
		sort: [{ column: "published_at", order: "desc" }],
		limit,
	};
	const key = JSON.stringify(query);
	if (!newsCache.has(key)) {
		newsCache.set(
			key,
			(async () => {
				try {
					const signal =
						typeof AbortSignal !== "undefined" && AbortSignal.timeout
							? AbortSignal.timeout(8000)
							: undefined;
					const res = await fetch(`/content.json?query_json=${encodeURIComponent(key)}`, { signal });
					if (!res.ok) {
						throw new Error(`content.json ${res.status}`);
					}
					const json = await res.json();
					return Array.isArray(json?.items)
						? json.items.filter((i) => i && i.id != null && i.url && i.title)
						: [];
				} catch (e) {
					newsCache.delete(key);
					return [];
				}
			})(),
		);
	}
	return newsCache.get(key);
}

// "14:32" for today, "20 aug. 2026" otherwise (Bucharest wall clock)
const newsTimeFormatter = new Intl.DateTimeFormat("ro-RO", {
	timeZone: "Europe/Bucharest",
	hour: "2-digit",
	minute: "2-digit",
	hour12: false,
});
const newsDateFormatter = new Intl.DateTimeFormat("ro-RO", {
	timeZone: "Europe/Bucharest",
	day: "2-digit",
	month: "short",
	year: "numeric",
});
export function formatNewsDate(iso) {
	const date = new Date(iso);
	if (!iso || isNaN(date.getTime())) {
		return "";
	}
	const sameDay = newsDateFormatter.format(date) === newsDateFormatter.format(new Date());
	return sameDay ? newsTimeFormatter.format(date) : newsDateFormatter.format(date);
}

// GA4 events for the FinanceStrip. The interaction goes in the EVENT NAME
// (finance_strip_open, finance_strip_news_click…) because GA4 reports by
// name out of the box, whereas a custom parameter only shows up once someone
// registers it as a custom dimension. The section (currency|fuel|index) rides
// in event_category and the specific thing (card key, article url…) in
// event_label — the two params the rest of the site already sends.
// Fail-silent: analytics must never break the island.
export function trackFinance(action, category = null, label = null, extra = {}) {
	try {
		if (typeof window === "undefined" || !window.dataLayer || typeof window.gtag !== "function") {
			return;
		}
		const params = { ...extra };
		if (category) params.event_category = category;
		if (label) params.event_label = String(label);
		window.gtag("event", `finance_strip_${action}`, params);
	} catch (e) {
		// ignore
	}
}

// deterministic sample payload for the ADMIN preview — mirrors the API shape
// so the editor sees the real strip with their own config applied (pinned
// currencies, fuel on/off). Values are plausible but fictitious.
const SAMPLE_RATES = {
	EUR: 5.0712, USD: 4.6830, GBP: 5.9412, CHF: 5.3108, XAU: 446.21,
	HUF: 1.2634, PLN: 1.1842, CZK: 0.2053, JPY: 3.1472, CAD: 3.4120,
	MDL: 0.2651, TRY: 0.1394, SEK: 0.4498, NOK: 0.4386, DKK: 0.6797, AUD: 3.0655,
};
export function sampleFinance(pinned = DEFAULT_CURRENCIES) {
	const today = new Date();
	const prev = new Date(today.getTime() - 864e5);
	//	local calendar date (toISOString would flip to yesterday before 03:00 EEST)
	const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 6e4).toISOString().slice(0, 10);
	const codes = [...new Set([...pinned, ...Object.keys(SAMPLE_RATES)])];
	const rates = codes.map((code, i) => {
		const multiplier = code === "HUF" || code === "JPY" ? 100 : 1;
		// Samples above are quoted BNR amounts; the API returns per-unit values.
		const rate = (SAMPLE_RATES[code] || 1 + ((i * 37) % 100) / 100) / multiplier;
		const drift = ((i % 3) - 1) * (code === "XAU" ? 1.8 : 0.0031) / multiplier;
		return { code, rate, multiplier, prev: rate - drift, change: drift };
	});
	return {
		bnr: { date: iso(today), prevDate: iso(prev), updated: today.toISOString(), rates },
		fuel: {
			date: iso(today),
			updated: today.toISOString(),
			prices: {
				benzina_standard: { min: 6.89, avg: 7.12, max: 7.49, prevAvg: 7.08 },
				benzina_premium: { min: 7.45, avg: 7.78, max: 8.19, prevAvg: 7.79 },
				motorina_standard: { min: 7.19, avg: 7.46, max: 7.83, prevAvg: 7.51 },
				motorina_premium: { min: 7.79, avg: 8.12, max: 8.55, prevAvg: 8.12 },
				gpl: { min: 3.29, avg: 3.48, max: 3.69, prevAvg: 3.48 },
			},
		},
	};
}
